import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@headlessui/react";
import { useAppSelector } from "../../../../app/hooks";
import AlertMessage, { AlertLevel } from "../../../../components/AlertMessage";
import { DistrictCreateEditModel, useEditDistrictMutation } from "../../districtsApiSlice";
import PrimaryDistrictInfoSection from "./PrimaryDistrictInfoSection";
import DistrictAddressInfoSection from "./DistrictAddressInfoSection";

interface DistrictInfoSectionProps {
	districtInfo: DistrictCreateEditModel;
	returnTo?: string;
}

type DistrictInfoState = {
	name: string;
	code: string;
	externalID: string;
	addressContactInfo: {
		address: string;
		state: string;
		city: string;
		zip: string;
		emailAddress: string;
		phone: string;
	};
	cleverClassLinkInfo: {
		cleverId: string;
		showCleverId: boolean;
		classLinkId: string;
		showClassLinkId: boolean;
	};
};

export default function DistrictInfoSection(props: DistrictInfoSectionProps) {
	const navigate = useNavigate();
	const { config } = useAppSelector((state) => state.site);

	const [saveError, setSaveError] = useState("");
	const [disableSave, setDisableSave] = useState(false);

	const [districtInfoState, setDistrictInfoState] = useState<DistrictInfoState>({
		name: props.districtInfo.name,
		code: props.districtInfo.code,
		externalID: props.districtInfo.externalID,
		addressContactInfo: {
			address: props.districtInfo.address,
			state: props.districtInfo.state,
			city: props.districtInfo.city,
			zip: props.districtInfo.zip,
			emailAddress: props.districtInfo.emailAddress,
			phone: props.districtInfo.phone,
		},
		cleverClassLinkInfo: {
			cleverId: props.districtInfo.cleverID,
			showCleverId: props.districtInfo.showCleverId,
			classLinkId: props.districtInfo.classLinkID,
			showClassLinkId: props.districtInfo.showClassLinkId,
		},
	});

	const setPrimaryInfo = (
		patch: Partial<Pick<DistrictInfoState, "name" | "code" | "externalID">>
	) => setDistrictInfoState((s) => ({ ...s, ...patch }));

	const setAddressInfo = (patch: Partial<DistrictInfoState["addressContactInfo"]>) =>
		setDistrictInfoState((s) => ({
			...s,
			addressContactInfo: { ...s.addressContactInfo, ...patch },
		}));

	const setCleverClassLinkInfo = (patch: Partial<DistrictInfoState["cleverClassLinkInfo"]>) =>
		setDistrictInfoState((s) => ({
			...s,
			cleverClassLinkInfo: { ...s.cleverClassLinkInfo, ...patch },
		}));

	const [showAddressSection, setShowAddressSection] = useState(false);

	function toggleShowAddressSection() {
		setShowAddressSection((val) => !val);
	}

	const [editDistrict] = useEditDistrictMutation();

	useEffect(() => {
		const hasEmptyRequiredFields = !districtInfoState.name || !districtInfoState.code;
		const currentModel = updateModel();
		const hasChanges = JSON.stringify(currentModel) !== JSON.stringify(props.districtInfo);

		if (hasEmptyRequiredFields || !hasChanges) {
			setDisableSave(true);
		} else {
			setDisableSave(false);
		}
	}, [districtInfoState]);

	function updateModel(): DistrictCreateEditModel {
		const model: DistrictCreateEditModel = {
			...props.districtInfo,
			name: districtInfoState.name,
			code: districtInfoState.code,
			externalID: districtInfoState.externalID,
			address: districtInfoState.addressContactInfo.address,
			city: districtInfoState.addressContactInfo.city,
			state: districtInfoState.addressContactInfo.state,
			zip: districtInfoState.addressContactInfo.zip,
			emailAddress: districtInfoState.addressContactInfo.emailAddress,
			phone: districtInfoState.addressContactInfo.phone,
		};
		return model;
	}

	function handleSave(e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) {
		e.preventDefault();
		const model = updateModel();

		if (model) {
			editDistrict(model)
				.unwrap()
				.then(() => {
					navigate(props.returnTo ?? `/districts`);
				})
				.catch((error) => {
					setSaveError(error.data);
					scrollTo(0, 0);
					console.log(error);
				});
		}
	}

	function handleCancel() {
		navigate(props.returnTo ?? `/districts`);
	}

	return (
		<section>
			{!config?.isUtahComposeSite && <hr className="my-6" />}
			<AlertMessage
				className="mb-2"
				type={AlertLevel.Error}
				message={saveError}
				showIcon={true}
				onClear={() => setSaveError("")}
			/>
			<PrimaryDistrictInfoSection
				value={{
					districtId: props.districtInfo.id,
					name: districtInfoState.name,
					code: districtInfoState.code,
					externalID: districtInfoState.externalID,
					cleverId: districtInfoState.cleverClassLinkInfo.cleverId,
					showCleverId: districtInfoState.cleverClassLinkInfo.showCleverId,
					classLinkId: districtInfoState.cleverClassLinkInfo.classLinkId,
					showClassLinkId: districtInfoState.cleverClassLinkInfo.showClassLinkId,
				}}
				onPrimaryInfoChange={setPrimaryInfo}
				onCleverClassLinkChange={setCleverClassLinkInfo}
				editMode
			/>
			<DistrictAddressInfoSection
				value={{
					address: districtInfoState.addressContactInfo.address,
					city: districtInfoState.addressContactInfo.city,
					state: districtInfoState.addressContactInfo.state,
					zip: districtInfoState.addressContactInfo.zip,
					emailAddress: districtInfoState.addressContactInfo.emailAddress,
					phone: districtInfoState.addressContactInfo.phone,
				}}
				onAddressChange={setAddressInfo}
				editMode
				showAddressSection={showAddressSection}
				toggleShowAddressSection={toggleShowAddressSection}
			/>
			<div className="flex items-center justify-end gap-x-4">
				<Button type="button" className="btn btn-naked" onClick={handleCancel}>
					Cancel
				</Button>
				<Button
					type="submit"
					className="btn btn-primary"
					disabled={disableSave}
					onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleSave(e)}>
					Save
				</Button>
			</div>
		</section>
	);
}
